import React, { useState } from 'react';
import { useParams } from 'react-router-dom';

import { Button, Flex, Group, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconX } from '@tabler/icons-react';

import { useAppDispatch } from '@/store/hooks';
import { useCancelAppointmentsMutation } from '@/store/slices/api/appointmentApiSlice';
import { patientsApiSlice } from '@/store/slices/api/patientsApiSlice';
import { API_SLICE } from '@/utils/constants/apiEndpoints';

import styles from '../DeviceScheduleModal.module.scss';
import { EditScheduleStepProps } from '../types';

const CancelActiveSchedules: React.FC<EditScheduleStepProps> = ({
  onBack,
  onClose,
  loading,
  setLoading,
  deviceInfo
}) => {
  const { id: patientId } = useParams() as { id: string };
  const [showConfirmation, setShowConfirmation] = useState(false);
  const dispatch = useAppDispatch();
  const [cancelAppointments] = useCancelAppointmentsMutation();
  const deviceId = deviceInfo?.deviceId;

  const handleCancelAll = async () => {
    if (!deviceId) {
      notifications.show({
        title: 'Error',
        message: 'Device information is missing',
        color: 'red',
        icon: <IconX size="1.1rem" />
      });
      return;
    }

    setLoading(true);

    try {
      await cancelAppointments({ deviceId }).unwrap();

      notifications.show({
        title: 'Success',
        message: 'Active schedules for this device have been cancelled.',
        color: 'green',
        icon: <IconCheck size="1.1rem" />,
        autoClose: 3000
      });

      dispatch(
        patientsApiSlice.util.invalidateTags([
          { type: API_SLICE.Patient, id: patientId },
          { type: API_SLICE.Patient, id: `${patientId}-schedules` }
        ])
      );

      setShowConfirmation(true);
    } catch (error) {
      console.error('Error cancelling appointments:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to cancel schedules. Please try again.',
        color: 'red',
        icon: <IconX size="1.1rem" />,
        autoClose: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setShowConfirmation(false);
    onClose();
  };

  if (showConfirmation) {
    return (
      <Stack gap={'xs'} px={20} pt={16}>
        <Title order={5} fw={600} mb={4} c={'plum.9'}>
          Schedules Cancelled
        </Title>
        <Text size="sm" mb={8}>
          Active remote schedules for this device have been cancelled.
        </Text>
        <Group className={styles.buttonGroup} justify="flex-end">
          <Button color="plum.9" onClick={handleClose}>
            Close
          </Button>
        </Group>
      </Stack>
    );
  }

  return (
    <Stack gap={'xs'}>
      <Flex px={20} pt={16} direction={'column'}>
        <Title order={5} fw={600} mb={4} c={'plum.9'}>
          Cancel All Active Schedules
        </Title>
        <Text size="xs" c={'plum'} mb={8}>
          This will cancel active remote schedules for this device. In-clinic and
          physician-requested dates stay. This action cannot be undone.
        </Text>
      </Flex>
      <Group className={styles.buttonGroup} justify="flex-end">
        <Button
          variant="outline"
          color="plum.9"
          onClick={onBack}
          className={styles.modalButton}
          disabled={loading}>
          Back
        </Button>
        <Button
          color="plum.9"
          onClick={handleCancelAll}
          className={styles.modalButton}
          disabled={loading || !deviceId}
          loading={loading}>
          Cancel All
        </Button>
      </Group>
    </Stack>
  );
};

export default CancelActiveSchedules;
